/** Disponibilidad producto y tipo de movimiento — inventario de gestión. */

import { incotermIndicador } from './inventarioOcUtils';

export const ESTADO_DISPONIBLE = 'Disponible';
export const ESTADO_NO_DISPONIBLE = 'No disponible';
export const ESTADO_EN_PROCESO = 'En proceso';
export const ESTADO_EN_PUERTO = 'En puerto';
export const ESTADO_CERRADO = 'Cerrado';
export const ESTADO_BORRADOR = 'Borrador';

export const ESTADOS_DISPONIBILIDAD = [
  ESTADO_DISPONIBLE,
  ESTADO_NO_DISPONIBLE,
  ESTADO_EN_PROCESO,
  ESTADO_EN_PUERTO,
  ESTADO_CERRADO,
  ESTADO_BORRADOR,
];

export const ESTADOS_MANUALES = [ESTADO_EN_PROCESO, ESTADO_CERRADO, ESTADO_BORRADOR];

export const TIPO_MOV_CANCELADO = 'cancelado';
export const TIPO_MOV_EN_TRANSITO = 'en_transito';
export const TIPO_MOV_PRODUCIDO = 'producido';
export const TIPO_MOV_INGRESO = 'ingreso';

/** Tipo de movimiento (distinto del tipo de documento NF). */
export const TIPOS_MOVIMIENTO_GESTION = [
  { value: TIPO_MOV_CANCELADO, label: 'Cancelado' },
  { value: TIPO_MOV_EN_TRANSITO, label: 'En tránsito' },
  { value: TIPO_MOV_PRODUCIDO, label: 'Producido' },
  { value: TIPO_MOV_INGRESO, label: 'Ingreso' },
];

const EXW_INCOTERM_ID = 7;

export function hoyLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function tipoMovimientoLabel(val) {
  const t = String(val ?? '').toLowerCase();
  const hit = TIPOS_MOVIMIENTO_GESTION.find((x) => x.value === t);
  if (hit) return hit.label;
  return val || '—';
}

/** "producido" solo aplica con incoterm EXW (no FOB ni otros). */
export function incotermPermiteProducido(nota, incoterms) {
  const id = nota?.incoterm_id;
  if (id != null && Number(id) === EXW_INCOTERM_ID) return true;
  const inc = (incoterms || []).find((i) => Number(i.id_incoterm) === Number(id));
  const nombre = String(inc?.nombre || nota?.incoterm_label || '').toUpperCase();
  if (nombre.includes('EXW')) return true;
  if (incotermIndicador(id, incoterms) === 3) return true;
  return false;
}

export function opcionesTipoMovimiento(nota, incoterms) {
  return TIPOS_MOVIMIENTO_GESTION.filter(
    (t) => t.value !== TIPO_MOV_PRODUCIDO || incotermPermiteProducido(nota, incoterms)
  );
}

export function normalizarTipoMovimiento(val, nota, incoterms) {
  const t = String(val ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  let canon = t;
  if (t === 'en_tránsito' || t === 'en_transito' || t === 'en transito') canon = TIPO_MOV_EN_TRANSITO;
  if (!TIPOS_MOVIMIENTO_GESTION.some((x) => x.value === canon)) canon = TIPO_MOV_EN_TRANSITO;
  if (canon === TIPO_MOV_PRODUCIDO && !incotermPermiteProducido(nota, incoterms)) {
    canon = TIPO_MOV_EN_TRANSITO;
  }
  return canon;
}

export function tieneFechaIngresoPuerto(nota) {
  const f = nota?.fecha_ingreso_puerto;
  return f != null && String(f).trim() !== '';
}

export function esTipoMovimientoCancelado(nota) {
  return String(nota?.tipo_movimiento ?? '').toLowerCase() === TIPO_MOV_CANCELADO;
}

export function esEstadoAutomatico(nota) {
  if (esTipoMovimientoCancelado(nota)) return true;
  if (tieneFechaIngresoPuerto(nota)) return true;
  return false;
}

export function resolverEstadoMercaderia(nota) {
  if (esTipoMovimientoCancelado(nota)) return ESTADO_NO_DISPONIBLE;
  if (tieneFechaIngresoPuerto(nota)) return ESTADO_EN_PUERTO;

  const cur = nota?.estado_mercaderia;
  if (nota?._estado_mercaderia_manual && ESTADOS_MANUALES.includes(cur)) return cur;
  if (ESTADOS_MANUALES.includes(cur)) return cur;

  return ESTADO_DISPONIBLE;
}

export function applyGestionNota(nota, patch = {}, { incoterms } = {}) {
  const next = { ...nota, ...patch };

  if (patch.tipo_movimiento !== undefined) {
    const prev = nota.tipo_movimiento;
    next.tipo_movimiento = normalizarTipoMovimiento(patch.tipo_movimiento, next, incoterms);
    if (next.tipo_movimiento !== prev) {
      next.fecha_mov_almacen = hoyLocalISO();
    }
  } else if (next.tipo_movimiento === TIPO_MOV_PRODUCIDO && !incotermPermiteProducido(next, incoterms)) {
    next.tipo_movimiento = TIPO_MOV_EN_TRANSITO;
  }

  if (patch.estado_mercaderia !== undefined) {
    if (ESTADOS_MANUALES.includes(patch.estado_mercaderia)) {
      next._estado_mercaderia_manual = true;
    } else if (patch.estado_mercaderia === ESTADO_DISPONIBLE) {
      next._estado_mercaderia_manual = false;
    }
  }

  if (patch.tipo_movimiento !== undefined || patch.fecha_ingreso_puerto !== undefined) {
    if (esTipoMovimientoCancelado(next) || tieneFechaIngresoPuerto(next)) {
      next._estado_mercaderia_manual = false;
    }
  }

  next.estado_mercaderia = resolverEstadoMercaderia(next);
  return next;
}

export function defaultsGestionNota(nota, { incoterms } = {}) {
  const esNueva = !nota.tipo_movimiento;
  return applyGestionNota(
    {
      ...nota,
      tipo_movimiento: nota.tipo_movimiento || TIPO_MOV_EN_TRANSITO,
      fecha_mov_almacen: nota.fecha_mov_almacen || (esNueva ? hoyLocalISO() : ''),
      fecha_ingreso_puerto: nota.fecha_ingreso_puerto ?? '',
      estado_mercaderia: nota.estado_mercaderia || ESTADO_DISPONIBLE,
    },
    {},
    { incoterms }
  );
}
